/**
 * ReferralFormField — leaf input renderer extracted from
 * MedicationReferralDialog.jsx (Feb 2026 Phase 2 follow-up cleanup).
 *
 * Responsibility: render ONE input element for a single dynamic
 * field definition.  Returns null for non-input field types
 * (section_header, divider, client_visibility_divider, client_*).
 * Those are kept inside the dialog itself because they need
 * dialog-scoped state (clientMedications, clientDiagnoses, users,
 * selectedNote, etc.) that doesn't belong in a leaf renderer.
 *
 * Supported field.type values:
 *   text, date, time, textarea, select, multiselect, radio, checkbox
 *
 * Props
 * ─────
 *   field      — the form-template field object (id, type, label,
 *                placeholder, required, options, width, etc.)
 *   formData   — the dialog's form-data object keyed by field.id
 *   setFormData — setter that takes the new full form-data object
 *
 * The Label element (above the input) and AI-Summarize button
 * (for the 'Mental Health Service Details' textarea) stay in the
 * dialog so this component remains pure and dialog-state-free.
 * Checkbox is special-cased: it ships with its own inline label.
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DebouncedInput, DebouncedTextarea } from '../../ui/debounced-textarea';

// Normalises an option (string or {value,label} object) → display label.
const getOptionLabel = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'string') return opt;
  return opt.label || opt.value || '';
};

// Normalises an option → stored value (matches legacy behavior).
const getOptionValue = (opt) =>
  typeof opt === 'object' ? (opt.value || opt.label) : opt;

export const ReferralFormField = ({ field, formData, setFormData }) => {
  if (!field || !field.type) return null;

  // Patch a single field id with a new value, preserving the rest
  // of the form-data object.  Centralised so every input type uses
  // the exact same update shape (was duplicated 8+ times before).
  const patch = (value) => setFormData({ ...formData, [field.id]: value });

  switch (field.type) {
    case 'text':
      return (
        <DebouncedInput
          id={`field-${field.id}`}
          type="text"
          placeholder={field.placeholder || ''}
          value={formData[field.id] || ''}
          onChange={(e) => patch(e.target.value)}
        />
      );

    case 'date':
      return (
        <Input
          id={`field-${field.id}`}
          type="date"
          value={formData[field.id] || ''}
          onChange={(e) => patch(e.target.value)}
        />
      );

    case 'time':
      return (
        <Input
          id={`field-${field.id}`}
          type="time"
          value={formData[field.id] || ''}
          onChange={(e) => patch(e.target.value)}
        />
      );

    case 'textarea':
      return (
        <DebouncedTextarea
          id={`field-${field.id}`}
          placeholder={field.placeholder || ''}
          value={formData[field.id] || ''}
          onChange={(e) => patch(e.target.value)}
          rows={field.rows || 3}
          debounceMs={100}
        />
      );

    case 'select':
      return (
        <Select value={formData[field.id] || ''} onValueChange={patch}>
          <SelectTrigger>
            <SelectValue placeholder={field.placeholder || 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((option, optIndex) => (
              <SelectItem key={optIndex} value={getOptionValue(option)}>
                {getOptionLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'multiselect': {
      const selected = formData[field.id] || [];
      return (
        <div className="relative">
          <div
            className="border rounded-md p-2 min-h-[38px] cursor-pointer bg-white hover:bg-gray-50"
            onClick={(e) => {
              const dropdown = e.currentTarget.nextElementSibling;
              if (dropdown) dropdown.classList.toggle('hidden');
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {selected.length > 0 ? (
                  selected.map((val, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {getOptionLabel(val)}
                      <button
                        type="button"
                        className="ml-1 hover:text-blue-900"
                        onClick={(e) => {
                          e.stopPropagation();
                          patch(selected.filter((v) => v !== val));
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">
                    {field.placeholder || 'Select options...'}
                  </span>
                )}
              </div>
              <span className="text-gray-400">▼</span>
            </div>
          </div>
          <div className="hidden absolute z-50 w-full mt-1 border rounded-md p-2 bg-white shadow-lg max-h-60 overflow-y-auto min-w-[300px]">
            {(field.options || []).map((option, optIndex) => {
              const optValue = getOptionValue(option);
              return (
                <div
                  key={optIndex}
                  className="flex items-start space-x-2 p-2 hover:bg-gray-50 rounded"
                >
                  <Checkbox
                    id={`${field.id}-${optIndex}`}
                    checked={selected.includes(optValue)}
                    onCheckedChange={(checked) => {
                      patch(
                        checked
                          ? [...selected, optValue]
                          : selected.filter((v) => v !== optValue),
                      );
                    }}
                    className="mt-0.5 shrink-0"
                  />
                  <Label
                    htmlFor={`${field.id}-${optIndex}`}
                    className="flex-1 font-normal cursor-pointer text-sm break-words whitespace-normal"
                  >
                    {getOptionLabel(option)}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    case 'radio': {
      const currentValue = formData[field.id];
      return (
        <div className="space-y-2 mt-2">
          {(field.options || []).map((option, idx) => {
            const optValue = getOptionValue(option);
            const isSelected = field.allowMultiple
              ? (Array.isArray(currentValue) ? currentValue.includes(optValue) : false)
              : currentValue === optValue;
            return (
              <div key={idx} className="flex items-center space-x-2">
                <input
                  type={field.allowMultiple ? 'checkbox' : 'radio'}
                  id={`${field.id}-${idx}`}
                  name={field.id}
                  value={optValue}
                  checked={isSelected}
                  onChange={(e) => {
                    if (field.allowMultiple) {
                      const currentValues = Array.isArray(currentValue)
                        ? currentValue
                        : (currentValue ? [currentValue] : []);
                      patch(
                        isSelected
                          ? currentValues.filter((v) => v !== optValue)
                          : [...currentValues, optValue],
                      );
                    } else {
                      patch(e.target.value);
                    }
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor={`${field.id}-${idx}`} className="font-normal cursor-pointer">
                  {getOptionLabel(option)}
                </Label>
              </div>
            );
          })}
        </div>
      );
    }

    case 'checkbox':
      // Checkbox ships with its own inline label — the dialog
      // suppresses the top-level Label for this case.
      return (
        <div className="flex items-center space-x-2 mt-2">
          <Checkbox
            id={`field-${field.id}`}
            checked={formData[field.id] || false}
            onCheckedChange={(checked) => patch(checked)}
          />
          <Label htmlFor={`field-${field.id}`} className="font-normal">
            {field.label}
          </Label>
        </div>
      );

    default:
      // section_header / divider / client_visibility_divider /
      // client_medications / client_diagnoses / client_diagnoses_selector /
      // client_insurance — all dialog-scoped, rendered there.
      return null;
  }
};

export default ReferralFormField;
